import React, { useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Avatar } from 'primereact/avatar';
import { Calendar } from 'primereact/calendar';
import { Menu } from 'primereact/menu';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { roundToNextHalfHour } from 'utils/utility_functions/timezone';
import moment from 'moment';

const getEventEndTime = (eventStartTime = new Date(), extraMinutes = 30) => {
  eventStartTime?.setMinutes(eventStartTime?.getMinutes() + extraMinutes);
  return new Date(eventStartTime);
};

const getEventTimeDifferenceInMinutes = (eventEndTime, eventStartTime) => {
  eventEndTime = new Date(eventEndTime);
  eventStartTime = new Date(eventStartTime);
  let timeDifference = (eventEndTime?.getTime() - eventStartTime?.getTime()) / 1000;
  timeDifference /= 60;

  let absoluteTimeDifference = Math.abs(Math.round(timeDifference));
  return absoluteTimeDifference;
};

const EventTime = ({ index }) => {
  const addTimePanelRef = useRef(null);

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const startTimeName = `actions[${index}].eventStartTime`;
  const endTimeName = `actions[${index}].eventEndTime`;
  const eventStartTimeWatched = watch(startTimeName);
  const eventEndTimeWatched = watch(endTimeName);

  const addTimePanelItems = [
    {
      label: '+ 15 Minutes',
      command: () => {
        addExtraTimeHandler(15);
      },

      disabled: getEventTimeDifferenceInMinutes(eventStartTimeWatched, eventEndTimeWatched) === 15,
    },
    {
      label: '+ 30 Minutes',
      command: () => {
        addExtraTimeHandler(30);
      },
      disabled: getEventTimeDifferenceInMinutes(eventStartTimeWatched, eventEndTimeWatched) === 30,
    },
    {
      label: '+ 1 Hour',
      command: () => {
        addExtraTimeHandler(60);
      },
      disabled: getEventTimeDifferenceInMinutes(eventStartTimeWatched, eventEndTimeWatched) === 60,
    },
    {
      label: '+ 2 Hours',
      command: () => {
        addExtraTimeHandler(120);
      },
      disabled: getEventTimeDifferenceInMinutes(eventStartTimeWatched, eventEndTimeWatched) === 120,
    },
  ];

  const addExtraTimeHandler = (extraTime) => {
    let startTime = eventStartTimeWatched;
    setValue(endTimeName, getEventEndTime(startTime, extraTime), { shouldValidate: true });
  };

  const handleEventStartTimeChange = (e, onChange) => {
    onChange(e.value);
    let from_time = new Date(e?.value);
    setValue(endTimeName, getEventEndTime(from_time, 30), { shouldValidate: true });
  };

  return (
    <div className="d-flex mt-2 align-items-center">
      <div className="d-flex flex-column w-100">
        <Controller
          name={startTimeName}
          control={control}
          defaultValue={roundToNextHalfHour(new Date())}
          rules={{ required: 'Required' }}
          render={({ field: { name, value, onChange } }) => (
            <Calendar
              name={name}
              value={value}
              onChange={(e) => handleEventStartTimeChange(e, onChange)}
              timeOnly
              hourFormat="12"
              placeholder="Start Time"
              className="input-shadow w-100"
              stepMinute={30}
            />
          )}
        />
        <RequiredValidation errors={errors} field={`actions.${index}.eventStartTime`} message="Start Time Required" />
      </div>

      {eventStartTimeWatched ? (
        <Avatar
          label="+"
          className="mx-2 p-3"
          style={{ backgroundColor: 'var(--gray-medium)' }}
          size="small"
          shape="circle"
          onClick={(e) => addTimePanelRef?.current?.toggle(e)}
        />
      ) : null}
      <span className="mx-2">:</span>

      <div className="d-flex flex-column w-100">
        <Controller
          name={endTimeName}
          control={control}
          defaultValue={roundToNextHalfHour(moment().add(30, 'minutes').toDate())}
          rules={{ required: 'Required' }}
          render={({ field: { name, value, onChange } }) => (
            <Calendar
              name={name}
              value={value}
              onChange={(e) => onChange(e.value)}
              timeOnly
              hourFormat="12"
              placeholder="End Time"
              className="input-shadow w-100"
              stepMinute={30}
            />
          )}
        />
        <RequiredValidation errors={errors} field={`actions.${index}.eventEndTime`} message="End Time Required" />
      </div>
      <Menu model={addTimePanelItems} popup ref={addTimePanelRef} />
    </div>
  );
};

export default EventTime;
